import { useEffect, useRef, useCallback, useState } from 'react';

type UseAutoPollingOptions = {
  interval?: number;
  enabled?: boolean;
  onPoll: () => void | Promise<void>;
};

export function useAutoPolling({ interval = 5000, enabled = true, onPoll }: UseAutoPollingOptions) {
  const [isPolling, setIsPolling] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onPollRef = useRef(onPoll);

  // Keep the ref updated with latest onPoll function
  useEffect(() => {
    onPollRef.current = onPoll;
  }, [onPoll]);

  const startPolling = useCallback(() => {
    if (!enabled) return;

    // Clear any existing interval to prevent duplicates
    if (intervalRef.current) {
      console.log('Clearing existing polling interval');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    console.log(`Starting auto-polling with ${interval}ms interval`);
    setIsPolling(true);

    const poll = async () => {
      try {
        const timestamp = new Date().toLocaleTimeString();
        console.log(`[${timestamp}] Auto-polling tick (interval: ${interval}ms)`);
        await onPollRef.current();
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // Poll immediately
    poll();

    // Then poll at intervals
    intervalRef.current = setInterval(poll, interval);
  }, [interval, enabled]); // Remove onPoll from dependencies

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Handle visibility change (pause when tab is hidden)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsPaused(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsPolling(false);
      } else {
        setIsPaused(false);
        // Restart polling when tab becomes visible again
        if (enabled && !intervalRef.current) {
          startPolling();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, startPolling]); // Now safe to include since startPolling is stable

  // Start/stop polling based on enabled flag
  useEffect(() => {
    if (enabled && !document.hidden) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [enabled, startPolling, stopPolling]);

  return {
    isPolling,
    isPaused
  };
}
